import { toSvg } from 'html-to-image';
import { saveAs } from 'file-saver';

// Elements/inputs marked with data-export-exclude, and empty inputs (so
// placeholder text never bakes into the export), are dropped from the capture.
function shouldExclude(node) {
  if (node.nodeType !== 1) return false;
  if (typeof node.hasAttribute === 'function' && node.hasAttribute('data-export-exclude')) {
    return true;
  }
  if (node.tagName === 'INPUT' && !node.value) {
    return true;
  }
  return false;
}

function sanitize(value, fallback) {
  const cleaned = (value || '').trim().replace(/[^a-zA-Z0-9-_]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned || fallback;
}

export function buildExportFileName(teamName, suffix) {
  const team = sanitize(teamName, 'draft');
  const tag = sanitize(suffix, 'export');
  const date = new Date().toISOString().slice(0, 10);
  return `${team}-${tag}-${date}.jpg`;
}

export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Not requestAnimationFrame: rAF callbacks are suspended entirely while the
// tab is hidden/unfocused (a background export tab, an automation driver
// that steals focus, alt-tabbing mid-export) and simply never fire again
// until it's visible — hanging the whole export indefinitely. setTimeout
// still gets throttled in background tabs but always eventually fires.
export function waitForNextFrame() {
  return wait(50);
}

// Hero images use loading="lazy" so off-screen scenario boards never start
// downloading until scrolled into view. The export captures the FULL board
// strip (including boards the user never scrolled to), so those images
// would otherwise sit as permanent spinners — force them to start loading now.
export function forceEagerImageLoad(container) {
  if (!container) return;
  container.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    img.loading = 'eager';
  });
}

// Resolves once every <img> under container has fired load/error, PLUS a
// settle buffer — HeroImage flips its "loading" React state and fades the
// image in via a 0.2s CSS transition on the load *event*, so the DOM can
// report img.complete/onload before the fade-in (and the React re-render
// that removes the spinner overlay) has actually finished painting.
export function waitForImagesLoaded(container, { timeoutMs = 8000, settleMs = 350 } = {}) {
  return new Promise((resolve) => {
    if (!container) return resolve();
    const imgs = Array.from(container.querySelectorAll('img'));
    let remaining = imgs.filter((img) => !img.complete).length;

    const settleThenResolve = () => wait(settleMs).then(resolve);
    if (remaining === 0) return settleThenResolve();

    const timer = setTimeout(settleThenResolve, timeoutMs);
    const onSettle = () => {
      remaining -= 1;
      if (remaining <= 0) {
        clearTimeout(timer);
        settleThenResolve();
      }
    };
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', onSettle, { once: true });
        img.addEventListener('error', onSettle, { once: true });
      }
    });
  });
}

// node.scrollWidth only reflects the actual content width when the row is
// genuinely overflowing (needs horizontal scroll). When content fits
// without scrolling — e.g. a single scenario board, or the Top-15 pool row
// on its own — scrollWidth just reports the CONTAINER's own (viewport-wide)
// box, not the (much narrower) content inside it. That falsely stretched a
// single board out to full viewport width. Measure the actual union of the
// row's children instead; only fall back to scrollWidth when it's genuinely
// larger (real overflow).
export function measureContentWidth(node) {
  if (node.scrollWidth > node.clientWidth) {
    return node.scrollWidth;
  }
  const children = Array.from(node.children);
  if (children.length === 0) return node.clientWidth;
  const rects = children.map((c) => c.getBoundingClientRect());
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  // Small buffer: board drop-shadows and hover/focus outlines extend a few
  // px past the geometric border box measured above, and get clipped
  // without it.
  const contentWidth = Math.ceil(right - left) + 16;
  return contentWidth || node.clientWidth;
}

// For a `flex-wrap: wrap` row (the Top-15 hero pool): how wide would it need
// to be to fit every item on one line? Temporarily forces nowrap + auto
// width to read the true unwrapped size, then reverts — synchronous, so
// nothing paints in between and there's no visible flicker.
export function measureUnwrappedWidth(node) {
  const prevWrap = node.style.flexWrap;
  const prevWidth = node.style.width;
  node.style.flexWrap = 'nowrap';
  node.style.width = 'max-content';
  const width = node.scrollWidth;
  node.style.flexWrap = prevWrap;
  node.style.width = prevWidth;
  return width;
}

// Loads a data URI as an <img>, decoded and ready to draw. html-to-image's
// own toCanvas() does this same load+decode internally but then wraps the
// resolve in a bare requestAnimationFrame — which, like our old
// waitForNextFrame, simply never fires while the tab is hidden/unfocused,
// hanging the whole export. Same fix, applied here since that code lives
// inside the library and can't be patched from here.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => {
      img.decode().then(() => resolve(img)).catch(() => resolve(img));
    };
    img.onerror = reject;
    img.src = src;
  });
}

// Renders a DOM node to an off-screen canvas, chrome excluded.
//
// Height is never forced here — sized from node.clientHeight (measured on
// the LIVE node, before any filtering), so forcing a height would bake in
// whatever the live layout happened to be at measurement time. Chrome that
// should be excluded from the export (button rows, drag handles) must
// actually be unmounted from the live DOM before calling this (e.g. gated
// on an `isExportingJpeg` render flag) — filtering them only out of the
// html-to-image clone leaves the canvas sized for the taller,
// chrome-still-present layout, showing as blank space below the shorter
// captured content.
export async function captureBoardCanvas(node, options = {}) {
  const { backgroundColor = '#0b0d14', pixelRatio = 2, forceFullWidth = false, width: explicitWidth } = options;
  if (!node) {
    throw new Error('captureBoardCanvas: no node to capture');
  }

  const svgOptions = {
    backgroundColor,
    filter: (n) => !shouldExclude(n),
    // Google Fonts is cross-origin — embedding it means several failing
    // fetches per capture (slow, and noisy in the console). The board's
    // fallback sans-serif is fine for a reference screenshot.
    skipFonts: true,
  };

  // node is a horizontally-scrolling strip (overflow-x auto) — its own
  // rendered box is clipped to the visible viewport. Force width to its
  // full scrollable size (or an explicit target, e.g. to align the Top-15
  // pool row with the — usually much wider — scenario board strip below
  // it) so everything is captured, not just whatever happened to be
  // scrolled into view.
  const width = explicitWidth || (forceFullWidth ? measureContentWidth(node) : node.clientWidth);
  const height = node.clientHeight;
  if (explicitWidth || forceFullWidth) {
    svgOptions.width = width;
    svgOptions.style = { width: `${width}px`, overflow: 'visible', transform: 'none' };
  }

  const svgDataUrl = await toSvg(node, svgOptions);
  const img = await loadImage(svgDataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;
  const ctx = canvas.getContext('2d');
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// Stacks canvases vertically (e.g. the Top-15 hero pool above the scenario
// boards) into one canvas, left-aligned, on the shared background.
export function stackCanvasesVertically(canvases, { backgroundColor = '#0b0d14', gap = 16 } = {}) {
  const valid = canvases.filter(Boolean);
  const width = Math.max(...valid.map((c) => c.width));
  const height = valid.reduce((sum, c) => sum + c.height, 0) + gap * Math.max(0, valid.length - 1);

  const combined = document.createElement('canvas');
  combined.width = width;
  combined.height = height;
  const ctx = combined.getContext('2d');
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, width, height);

  let y = 0;
  valid.forEach((canvas) => {
    ctx.drawImage(canvas, 0, y);
    y += canvas.height + gap;
  });

  return combined;
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

export async function saveCanvasAsJpeg(canvas, fileName, quality = 0.95) {
  const blob = await canvasToBlob(canvas, quality);
  saveAs(blob, fileName);
}
